/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { Modal, Descriptions, Tag, Space, Typography, Divider, Empty, Spin, Button, Select, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { commonStyles } from '../../styles/commonStyles';
import { useGetEventByIdQuery, useUpdateEventMutation } from '../../api/plantingEventsApi';
import { skipToken } from '@reduxjs/toolkit/query';
import dayjs from 'dayjs';
import { EventKindTag } from '../../utils/eventKindHelpers';
import { useGetSaveUsersQuery } from '../../api/saveUsersApi';
import { useSave } from '../../contexts/WorldContext';

const { Text, Title } = Typography;

interface EventDetailsModalProps {
  eventId: string | null;
  farmId: string;
  open: boolean;
  onClose: () => void;
  canEdit?: boolean; // Право на редактирование события
}

/**
 * Модалка для просмотра деталей события (задачи)
 */
export const EventDetailsModal: React.FC<EventDetailsModalProps> = ({
  eventId,
  farmId,
  open,
  onClose,
  canEdit = false,
}) => {
  const save = useSave();
  const [isEditingAssignment, setIsEditingAssignment] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data: event, isLoading } = useGetEventByIdQuery(
    eventId && open ? { saveId: save.id, eventId } : skipToken
  );

  const { data: farmUsers } = useGetSaveUsersQuery(save.id, { skip: !open || !canEdit });

  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();

  const getStatusTag = (status?: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      planned: { color: 'default', text: 'Запланировано' },
      pending: { color: 'orange', text: 'Ожидает' },
      in_progress: { color: 'blue', text: 'Выполняется' },
      completed: { color: 'green', text: 'Завершено' },
      cancelled: { color: 'red', text: 'Отменено' },
    };
    const config = statusConfig[status || 'planned'] || statusConfig.planned;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getPlantingTypeTag = (type?: string) => {
    if (type === 'batch') {
      return <Tag color="purple">Групповая посадка</Tag>;
    }
    if (type === 'single') {
      return <Tag color="blue">Единичная посадка</Tag>;
    }
    return null;
  };

  const getDecisionTag = (decisionState?: string) => {
    if (decisionState === 'proposed') return <Tag color="gold">Требует проверки</Tag>;
    if (decisionState === 'rejected') return <Tag color="red">Отклонено</Tag>;
    return <Tag color="green">Подтверждено</Tag>;
  };

  return (
    <Modal
      title={event ? `Событие #${event.id}` : 'Детали события'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      {isLoading ? (
        <div css={css([commonStyles.textCenter, { padding: '40px' }])}>
          <Spin size="large" />
        </div>
      ) : !event ? (
        <Empty description="Событие не найдено" />
      ) : (
        <Space direction="vertical" css={commonStyles.fullWidth} size="large">
          {/* Основная информация */}
          <div>
            <Title level={5}>Основная информация</Title>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Статус">
                {getStatusTag(event.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Тип">
                <Space>
                  <Tag>{event.type}</Tag>
                  <EventKindTag kind={event.kind} showIcon={true} />
                </Space>
              </Descriptions.Item>
              {event.templateName && (
                <Descriptions.Item label="Шаблон">
                  <Text strong>{event.templateName}</Text>
                  {event.templateDescription && (
                    <>
                      <br />
                      <Text type="secondary" css={commonStyles.tinyText}>
                        {event.templateDescription}
                      </Text>
                    </>
                  )}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Плановая дата">
                {event.plannedDate
                  ? dayjs(event.plannedDate).format('DD.MM.YYYY')
                  : '—'}
              </Descriptions.Item>
              {event.actualDate && (
                <Descriptions.Item label="Фактическая дата">
                  {dayjs(event.actualDate).format('DD.MM.YYYY HH:mm')}
                </Descriptions.Item>
              )}
              {event.notes && (
                <Descriptions.Item label="Примечания">
                  {event.notes}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Статус проверки">
                <Space>
                  {getDecisionTag(event.decisionState)}
                  {event.sourceType && <Tag>{event.sourceType}</Tag>}
                  {event.sourceConfidence !== null && event.sourceConfidence !== undefined && (
                    <Tag>{Math.round(Number(event.sourceConfidence) * 100)}%</Tag>
                  )}
                </Space>
              </Descriptions.Item>
              {event.decisionNote && (
                <Descriptions.Item label="Комментарий решения">
                  {event.decisionNote}
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>

          {/* Информация о культуре и посадке */}
          {(event.cropName || event.plantingType) && (
            <div>
              <Title level={5}>Культура и посадка</Title>
              <Descriptions column={1} bordered size="small">
                {event.cropName && (
                  <Descriptions.Item label="Культура">
                    <Text strong>{event.cropName}</Text>
                    {event.cropLatinName && (
                      <>
                        <br />
                        <Text type="secondary" italic css={commonStyles.tinyText}>
                          {event.cropLatinName}
                        </Text>
                      </>
                    )}
                  </Descriptions.Item>
                )}
                {(event.fieldName || event.plotName || event.bedName) && (
                  <Descriptions.Item label="Локация">
                    <Space direction="vertical" size={0}>
                      {event.bedName && (
                        <Text strong>{event.bedName}</Text>
                      )}
                      {event.plotName && (
                        <Text type={event.bedName ? 'secondary' : undefined}>
                          {event.plotName}
                        </Text>
                      )}
                      {event.fieldName && (
                        <Text type={event.bedName || event.plotName ? 'secondary' : undefined}>
                          {event.fieldName}
                        </Text>
                      )}
                    </Space>
                  </Descriptions.Item>
                )}
                {event.plantingType && (
                  <Descriptions.Item label="Тип посадки">
                    {getPlantingTypeTag(event.plantingType)}
                  </Descriptions.Item>
                )}
                {event.plantingType === 'batch' && event.plantingQuantity && (
                  <Descriptions.Item label="Количество в партии">
                    <Text strong>{event.plantingQuantity}</Text> единиц
                  </Descriptions.Item>
                )}
                {event.quantityAffected && (
                  <Descriptions.Item label="Затронуто единиц">
                    <Text strong css={css({ color: '#1890ff' })}>
                      {event.quantityAffected}
                    </Text>{' '}
                    {event.plantingQuantity && `из ${event.plantingQuantity}`}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          )}

          {/* Назначение и выполнение */}
          <div>
            <Title level={5}>
              Назначение и выполнение
              {canEdit && event.status !== 'completed' && event.status !== 'cancelled' && (
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setIsEditingAssignment(!isEditingAssignment);
                    if (!isEditingAssignment) {
                      setSelectedUserId(event.assignedTo?.id || null);
                    }
                  }}
                  css={css({ marginLeft: '8px' })}
                >
                  {isEditingAssignment ? 'Отменить' : 'Изменить назначение'}
                </Button>
              )}
            </Title>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Назначено">
                {isEditingAssignment ? (
                  <Space>
                    <Select
                      css={css({ width: 300 })}
                      placeholder="Выберите исполнителя"
                      value={selectedUserId}
                      onChange={setSelectedUserId}
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={[
                        { value: null, label: '(Не назначено)' },
                        ...(farmUsers || []).map((item) => ({
                          value: item.user.id,
                          label: `${item.user.username || item.user.email} (${item.membership.role.code})`,
                        })),
                      ]}
                    />
                    <Button
                      type="primary"
                      size="small"
                      loading={isUpdating}
                      onClick={async () => {
                        try {
                          await updateEvent({
                            saveId: save.id,
                            eventId: event.id,
                            payload: { assignedTo: selectedUserId || undefined },
                          }).unwrap();
                          message.success('Назначение изменено');
                          setIsEditingAssignment(false);
                        } catch (error) {
                          message.error('Ошибка изменения назначения');
                        }
                      }}
                    >
                      Сохранить
                    </Button>
                  </Space>
                ) : event.assignedTo ? (
                  <Space direction="vertical" size={0}>
                    <Text>{event.assignedTo.name}</Text>
                    {event.assignedAt && (
                      <Text type="secondary" css={commonStyles.tinyText}>
                        {dayjs(event.assignedAt).format('DD.MM.YYYY HH:mm')}
                      </Text>
                    )}
                  </Space>
                ) : (
                  <Text type="secondary">Не назначено</Text>
                )}
              </Descriptions.Item>
              {event.completedBy && (
                <Descriptions.Item label="Завершено">
                  <Space direction="vertical" size={0}>
                    <Text>Пользователь ID: {event.completedBy}</Text>
                    {event.actualDate && (
                      <Text type="secondary" css={commonStyles.tinyText}>
                        {dayjs(event.actualDate).format('DD.MM.YYYY HH:mm')}
                      </Text>
                    )}
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>

          {/* Batch информация */}
          {event.isBatch && (
            <div>
              <Title level={5}>Массовое мероприятие</Title>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Тип">
                  <Tag color="purple">Родительское событие (контейнер)</Tag>
                </Descriptions.Item>
                {event.batchTotal !== undefined && (
                  <Descriptions.Item label="Всего дочерних событий">
                    {event.batchTotal}
                  </Descriptions.Item>
                )}
                {event.batchCompleted !== undefined && (
                  <Descriptions.Item label="Завершено">
                    {event.batchCompleted} / {event.batchTotal}
                  </Descriptions.Item>
                )}
                {event.batchProgress !== undefined && (
                  <Descriptions.Item label="Прогресс">
                    {event.batchProgress}%
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          )}

          {/* Атрибуты события */}
          {event.properties && event.properties.length > 0 && (
            <div>
              <Title level={5}>Атрибуты и измерения</Title>
              <Descriptions column={1} bordered size="small">
                {event.properties.map((attr: any) => (
                  <Descriptions.Item
                    key={attr.propertyId}
                    label={
                      <Space>
                        <Text strong>{attr.propertyName}</Text>
                        {attr.propertyCode && (
                          <Text type="secondary" css={commonStyles.tinyText}>
                            ({attr.propertyCode})
                          </Text>
                        )}
                      </Space>
                    }
                  >
                    {attr.valueNumber !== null && attr.valueNumber !== undefined && (
                      <Tag color="blue">{attr.valueNumber}</Tag>
                    )}
                    {attr.valueText && <Tag color="green">{attr.valueText}</Tag>}
                    {attr.valueBoolean !== null && attr.valueBoolean !== undefined && (
                      <Tag color={attr.valueBoolean ? 'success' : 'default'}>
                        {attr.valueBoolean ? 'Да' : 'Нет'}
                      </Tag>
                    )}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </div>
          )}

          {/* ID информация */}
          <div>
            <Divider css={css({ margin: '8px 0' })} />
            <Space size="small" wrap>
              <Text type="secondary" css={commonStyles.tinyText}>
                ID события: {event.id}
              </Text>
              {event.plantingId && (
                <Text type="secondary" css={commonStyles.tinyText}>
                  | ID посадки: {event.plantingId}
                </Text>
              )}
              {event.parentEventId && (
                <Text type="secondary" css={commonStyles.tinyText}>
                  | Родительское событие: {event.parentEventId}
                </Text>
              )}
            </Space>
          </div>
        </Space>
      )}
    </Modal>
  );
};
